import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import RichTextEditor from '../content/RichTextEditor';
import { prepareHTMLContent } from '../../utils/htmlUtils';
import { showToast } from '../../utils/toast';

interface Announcement {
  id: string;
  category_id: string;
  title: string;
  content: string;
  content_html: string;
  type: 'info' | 'warning' | 'success' | 'announcement';
  is_active: boolean;
  created_at: string;
  expires_at?: string;
}

interface AdminAnnouncementsProps {
  categoryId: string;
  categorySlug: string;
  readonly?: boolean;
}

const AdminAnnouncements: React.FC<AdminAnnouncementsProps> = ({ 
  categoryId, 
  categorySlug, 
  readonly = false 
}) => {
  const { user } = useAuth();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    content_html: '',
    type: 'info' as 'info' | 'warning' | 'success' | 'announcement',
    expires_at: ''
  });

  const isAdmin = user?.role === 'admin';

  console.log('AdminAnnouncements - User:', user);
  console.log('AdminAnnouncements - User role:', user?.role);
  console.log('AdminAnnouncements - isAdmin:', isAdmin);
  console.log('AdminAnnouncements - readonly:', readonly);

  useEffect(() => {
    loadAnnouncement();
  }, [categoryId]);

  const loadAnnouncement = async () => {
    try {
      const { data, error } = await supabase
        .from('forum_category_announcements')
        .select('*')
        .eq('category_id', categoryId)
        .eq('is_active', true)
        .maybeSingle(); // Get single result or null

      if (error) throw error;
      
      console.log('Loaded announcement data:', data);
      if (data) {
        console.log('Announcement content:', data.content);
        console.log('Announcement content_html:', data.content_html);
      }
      
      // Filter out expired announcements
      if (data && data.expires_at) {
        // Parse the datetime in UTC and compare with current UTC time
        const expirationDate = new Date(data.expires_at);
        const currentDate = new Date();
        
        // Ensure we're comparing in the same timezone
        const isExpired = expirationDate.getTime() <= currentDate.getTime();
        
        console.log('Checking expiration:');
        console.log('- Expiration date from DB:', data.expires_at);
        console.log('- Parsed expiration date (UTC):', expirationDate.toISOString());
        console.log('- Local expiration display:', expirationDate.toLocaleString());
        console.log('- Current date (UTC):', currentDate.toISOString());
        console.log('- Current local time:', currentDate.toLocaleString());
        console.log('- Is expired?', isExpired);
        console.log('- Time difference (ms):', expirationDate.getTime() - currentDate.getTime());
        console.log('- Time difference (minutes):', Math.round((expirationDate.getTime() - currentDate.getTime()) / (1000 * 60)));
        
        if (isExpired) {
          console.log('Announcement has expired, hiding it');
          setAnnouncement(null);
          // Optionally deactivate expired announcement
          await supabase
            .from('forum_category_announcements')
            .update({ is_active: false })
            .eq('id', data.id);
        } else {
          setAnnouncement(data);
        }
      } else {
        setAnnouncement(data);
      }
    } catch (error) {
      console.error('Error loading announcement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Submitting announcement with form data:', formData);
    console.log('Content HTML being saved:', formData.content_html);
    
    // Convert datetime-local input to ISO string for database storage
    let expiresAtISO = null;
    if (formData.expires_at) {
      // datetime-local gives us YYYY-MM-DDTHH:MM format in local time
      // Convert it to ISO string for consistent storage
      const localDate = new Date(formData.expires_at);
      expiresAtISO = localDate.toISOString();
      
      console.log('DateTime conversion for submission:');
      console.log('- Form input value:', formData.expires_at);
      console.log('- Parsed local date:', localDate);
      console.log('- ISO string for DB:', expiresAtISO);
      console.log('- Local display:', localDate.toLocaleString());
    }
    
    try {
      // Use the upsert function to handle single announcement per category
      const { data, error } = await supabase.rpc('upsert_category_announcement', {
        p_category_id: categoryId,
        p_title: formData.title,
        p_content: formData.content,
        p_content_html: formData.content_html,
        p_type: formData.type,
        p_expires_at: expiresAtISO,
        p_created_by: user?.id
      });

      if (error) throw error;

      console.log('Announcement saved successfully:', data);
      setFormData({ title: '', content: '', content_html: '', type: 'info', expires_at: '' });
      setShowForm(false);
      loadAnnouncement();
    } catch (error) {
      console.error('Error creating/updating announcement:', error);
    }
  };

  const handleDelete = async () => {
    if (!announcement || !window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      console.log('Attempting to delete announcement:', announcement.id);
      console.log('Current user:', user);
      console.log('User role:', user?.role);
      
      const { error } = await supabase
        .from('forum_category_announcements')
        .update({ is_active: false })
        .eq('id', announcement.id);

      if (error) {
        console.error('Delete error details:', error);
        // Try alternative approach with direct deletion if update fails
        if (error.code === '42501') {
          console.log('Trying direct deletion due to RLS policy issue');
          const { error: deleteError } = await supabase
            .from('forum_category_announcements')
            .delete()
            .eq('id', announcement.id);
          
          if (deleteError) {
            console.error('Direct deletion also failed:', deleteError);
            throw deleteError;
          }
        } else {
          throw error;
        }
      }
      
      console.log('Announcement deleted successfully');
      loadAnnouncement();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      showToast('Failed to delete announcement. Please check your admin permissions.', 'error');
    }
  };

  const handleEdit = () => {
    if (announcement) {
      // Convert ISO string to datetime-local format while preserving local timezone
      let expiresAtValue = '';
      if (announcement.expires_at) {
        const expiresDate = new Date(announcement.expires_at);
        // Get local time components to avoid timezone shifts
        const year = expiresDate.getFullYear();
        const month = String(expiresDate.getMonth() + 1).padStart(2, '0');
        const day = String(expiresDate.getDate()).padStart(2, '0');
        const hours = String(expiresDate.getHours()).padStart(2, '0');
        const minutes = String(expiresDate.getMinutes()).padStart(2, '0');
        expiresAtValue = `${year}-${month}-${day}T${hours}:${minutes}`;
        
        console.log('Edit form datetime conversion:');
        console.log('- Original expires_at:', announcement.expires_at);
        console.log('- Parsed date:', expiresDate);
        console.log('- Local string:', expiresDate.toLocaleString());
        console.log('- Form input value:', expiresAtValue);
      }
      
      setFormData({
        title: announcement.title,
        content: announcement.content,
        content_html: announcement.content_html || announcement.content,
        type: announcement.type,
        expires_at: expiresAtValue
      });
      setShowForm(true);
    }
  };

  const getAnnouncementIcon = (type: string) => {
    switch (type) {
      case 'warning': return 'bi-exclamation-triangle-fill';
      case 'success': return 'bi-check-circle-fill';
      case 'announcement': return 'bi-megaphone-fill';
      default: return 'bi-info-circle-fill';
    }
  };

  const getAnnouncementClass = (type: string) => {
    switch (type) {
      case 'warning': return 'announcement-warning';
      case 'success': return 'announcement-success';
      case 'announcement': return 'announcement-primary';
      default: return 'announcement-info';
    }
  };

  if (loading) {
    return <div className="announcements-loading">Loading announcements...</div>;
  }

  return (
    <div className="admin-announcements">
      {announcement && (
        <div className="announcements-list">
          <div 
            className={`announcement-item ${getAnnouncementClass(announcement.type)}`}
          >
            <div className="announcement-content">
              <div className="announcement-header">
                <i className={getAnnouncementIcon(announcement.type)}></i>
                <span className="announcement-title">{announcement.title}</span>
                {isAdmin && !readonly && (
                  <div className="announcement-actions">
                    <button 
                      className="announcement-edit"
                      onClick={handleEdit}
                      title="Edit announcement"
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button 
                      className="announcement-delete"
                      onClick={handleDelete}
                      title="Delete announcement"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                )}
              </div>
              
              <div 
                className="announcement-text"
                dangerouslySetInnerHTML={{ 
                  __html: prepareHTMLContent(announcement.content_html || announcement.content)
                }}
              />
              {announcement.expires_at && (
                <div className="announcement-expires">
                  Expires: {new Date(announcement.expires_at).toLocaleString(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isAdmin && !readonly && (
        <div className="admin-announcement-controls">
          {!showForm ? (
            <button 
              className="add-announcement-btn"
              onClick={() => setShowForm(true)}
            >
              <i className="bi bi-plus"></i>
              {announcement ? 'Edit Announcement' : 'Add Announcement'}
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="announcement-form">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Announcement title..."
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="form-input"
                />
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  className="form-select"
                >
                  <option value="info">Info</option>
                  <option value="announcement">Announcement</option>
                  <option value="warning">Warning</option>
                  <option value="success">Success</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Content</label>
                <RichTextEditor
                  content={formData.content_html}
                  onChange={(html) => {
                    // Extract text content from HTML for the content field
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = html;
                    const textContent = tempDiv.textContent || tempDiv.innerText || '';
                    
                    setFormData({ 
                      ...formData, 
                      content_html: html,
                      content: textContent 
                    });
                  }}
                  placeholder="Write your announcement content here..."
                  className="announcement-editor"
                />
              </div>
              
              <div className="form-row">
                <input
                  type="datetime-local"
                  placeholder="Expires at (optional)"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="form-input"
                />
                <div className="form-actions">
                  <button type="submit" className="btn-save">
                    {announcement ? 'Update' : 'Save'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowForm(false);
                      setFormData({ title: '', content: '', content_html: '', type: 'info', expires_at: '' });
                    }}
                    className="btn-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncements;
